'use client';

import { useEffect, useRef, useState } from 'react';
import { DEFAULT_MEDIA_ROOT, normalizePath } from '../../_utils';
import type { ClawFileItem, SessionMode } from '../../types';
import {
  buildYoloSessionMediaFolderPath,
  getFolderName,
  getParentFolderPath,
  WELCOME_MEDIA_FOLDER_KEY,
} from './chat-scene-paths';

interface UseSessionMediaRestoreOptions {
  activeSessionId: string | null;
  activeSessionMode?: SessionMode;
  clawFolderPath: string;
  sessionMediaFolderMap: Record<string, string>;
  setClawFolderPath: (path: string) => void;
  setSessionMediaFolder: (sessionId: string, folderPath: string) => void;
  removeSessionMediaFolder: (sessionId: string) => void;
  listClawFolder: (options: { folderPath: string; pageSize?: number }) => Promise<{
    data: ClawFileItem[] | null;
    error: unknown;
  }>;
}

export const useSessionMediaRestore = ({
  activeSessionId,
  activeSessionMode,
  clawFolderPath,
  sessionMediaFolderMap,
  setClawFolderPath,
  setSessionMediaFolder,
  removeSessionMediaFolder,
  listClawFolder,
}: UseSessionMediaRestoreOptions) => {
  const mediaRestoreRef = useRef<{ syncKey: string | null; restoringKey: string | null; validatingKey: string | null }>(
    {
      syncKey: null,
      restoringKey: null,
      validatingKey: null,
    },
  );
  const [restoringSessionMedia, setRestoringSessionMedia] = useState(false);

  useEffect(() => {
    const folderKey = activeSessionId || WELCOME_MEDIA_FOLDER_KEY;
    const bound = sessionMediaFolderMap[folderKey];
    const expectedPath = activeSessionId ? buildYoloSessionMediaFolderPath(activeSessionId) : DEFAULT_MEDIA_ROOT;
    const targetPath = bound || expectedPath;
    const syncKey = `${folderKey}:${activeSessionMode ?? ''}`;
    if (mediaRestoreRef.current.syncKey !== syncKey) {
      mediaRestoreRef.current.syncKey = syncKey;

      if (activeSessionId && activeSessionMode !== 'yolo' && activeSessionMode !== undefined) {
        setRestoringSessionMedia(false);
        return;
      }
      if (targetPath === clawFolderPath) {
        if (bound !== targetPath) setSessionMediaFolder(folderKey, targetPath);
        setRestoringSessionMedia(false);
        return;
      }
      const folderKeyAtCall = folderKey;
      mediaRestoreRef.current.restoringKey = folderKeyAtCall;
      mediaRestoreRef.current.validatingKey = folderKeyAtCall;
      setRestoringSessionMedia(true);
      setClawFolderPath(targetPath);
      void (async () => {
        const normalizedTargetPath = normalizePath(targetPath);
        const parentPath = getParentFolderPath(normalizedTargetPath) || DEFAULT_MEDIA_ROOT;
        const targetFolderName = getFolderName(normalizedTargetPath);
        const { data, error } = await listClawFolder({ folderPath: parentPath, pageSize: 100 });
        const targetFolderExists =
          normalizedTargetPath === DEFAULT_MEDIA_ROOT ||
          !!data?.some((item) => item.type === 'folder' && normalizePath(item.path) === normalizedTargetPath) ||
          !!data?.some((item) => item.type === 'folder' && item.name === targetFolderName);
        if (mediaRestoreRef.current.restoringKey !== folderKeyAtCall) {
          if (mediaRestoreRef.current.validatingKey === folderKeyAtCall) {
            mediaRestoreRef.current.validatingKey = null;
          }
          return;
        }
        if (mediaRestoreRef.current.validatingKey === folderKeyAtCall) {
          mediaRestoreRef.current.validatingKey = null;
        }
        if (error || !targetFolderExists) {
          removeSessionMediaFolder(folderKeyAtCall);
          setClawFolderPath(DEFAULT_MEDIA_ROOT);
          setRestoringSessionMedia(false);
          return;
        }
        setSessionMediaFolder(folderKeyAtCall, targetPath);
        setRestoringSessionMedia(false);
      })();
      return;
    }

    const isPathUnderMediaRoot =
      clawFolderPath === DEFAULT_MEDIA_ROOT || clawFolderPath.startsWith(`${DEFAULT_MEDIA_ROOT}/`);
    if (
      clawFolderPath &&
      clawFolderPath !== bound &&
      isPathUnderMediaRoot &&
      mediaRestoreRef.current.validatingKey !== folderKey
    ) {
      setSessionMediaFolder(folderKey, clawFolderPath);
    }
  }, [
    activeSessionId,
    activeSessionMode,
    clawFolderPath,
    sessionMediaFolderMap,
    setClawFolderPath,
    setSessionMediaFolder,
    removeSessionMediaFolder,
    listClawFolder,
  ]);

  return { restoringSessionMedia };
};
